import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  ActionMenu,
  Badge,
  BarChartIcon,
  BoltIcon,
  Button,
  ClockIcon,
  Column,
  CopyableItem,
  EyeIcon,
  FileTextIcon,
  Inline,
  ListIcon,
  Meter,
  Table,
  cssColorFromBasedOnPercentage,
} from "@KristianIV/common-ui";
import { tutToDate } from "../util";
import FastTrackBadge from "./FastTrackBadge";
import HostWarningBadge from "./HostWarningBadge";
import { FastTrackScout } from "./model/fastTrack";
import { Observation } from "./model/observation";
import { OfflineConfig } from "./model/organisation";
import { Scout, ScoutState } from "./model/scout";
import {
  DeviceRow,
  NOISE_RANGE_DBFS,
  Override,
  buildDeviceRows,
  countDevices,
  noiseMeterValue,
  noisePercentForColor,
  overrideText,
  shortDeviceId,
} from "./deviceRows";
import { isOfflineState, resolveOfflineThresholds, scoutStateColor, stateSortOrder } from "./scoutState";
import styles from "./OrganisationPage.module.css";

/** Badges shown per row before the rest folds into a "+n" badge; the device page lists them all. */
const MAX_OVERRIDE_BADGES = 3;

export const devicePath = (organisationId: string, deviceId: string) => `/organisation/${organisationId}/device/${deviceId}`;
export const deviceEventsPath = (organisationId: string, deviceId: string) =>
  `/incidents?organisation-id=${organisationId}&device-id=${deviceId}`;
export const deviceLogsPath = (organisationId: string, deviceId: string) =>
  `/logs?organisation-id=${organisationId}&device-id=${deviceId}`;

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Time only when the update is from today, date only otherwise, so a stale device stands out; the device page has the full timestamp. */
export const formatLastUpdate = (tut: number) => {
  const date = tutToDate(tut);
  return sameDay(date, new Date()) ? date.toLocaleTimeString() : date.toLocaleDateString();
};

export const NoiseMeter = ({ dbfs, size = "small" }: { dbfs: number; size?: "small" | "normal" }) => (
  <Meter
    size={size}
    value={noiseMeterValue(dbfs)}
    max={NOISE_RANGE_DBFS}
    valueLabel={`${dbfs.toFixed(0)} dBfs`}
    color={cssColorFromBasedOnPercentage(noisePercentForColor(dbfs))}
    className={size === "normal" ? styles.meter : undefined}
  />
);

interface Props {
  organisationId: string;
  scouts: Scout[];
  observations: { [deviceId: string]: Observation[] };
  fastTrackScouts: FastTrackScout[];
  /** The organisation's offline overrides, if it has any. */
  offline?: OfflineConfig;
}

const DevicesTable = ({ organisationId, scouts, observations, fastTrackScouts, offline }: Props) => {
  const history = useHistory();
  const [showOffline, setShowOffline] = useState(false);

  // As in commander: ignored scouts (silent past the hide threshold, or hidden by config) are never
  // listed; showing offline scouts extends the hide threshold to a week so recently silent ones stay visible.
  const thresholds = useMemo(() => resolveOfflineThresholds(offline, showOffline), [offline, showOffline]);
  const rows = useMemo(
    () =>
      buildDeviceRows(scouts, observations, fastTrackScouts, thresholds).filter(
        (row) => row.state !== ScoutState.OfflineIgnore && (showOffline || row.state !== ScoutState.Offline)
      ),
    [scouts, observations, fastTrackScouts, thresholds, showOffline]
  );
  const counts = countDevices(scouts, thresholds);

  const columns: Column<DeviceRow, any>[] = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      onCellClick: (row) => history.push(devicePath(organisationId, row.deviceId)),
      render: (_, row) => (
        <div>
          <div className={styles.deviceName}>{row.name}</div>
          <div className={styles.deviceLine}>{row.deviceLine}</div>
        </div>
      ),
    },
    {
      key: "deviceId",
      label: "ID",
      hideOnMobile: true,
      render: (_, row) => (
        <CopyableItem
          value={row.deviceId}
          displayValue={shortDeviceId(row.deviceId)}
          label="device ID"
          state={isOfflineState(row.state) ? "Offline" : undefined}
        />
      ),
    },
    {
      key: "stateLabel",
      label: "State",
      className: styles.nowrap,
      sortable: true,
      sortFn: (a, b) => stateSortOrder(a.state) - stateSortOrder(b.state),
      render: (_, row) => (
        <Badge color={scoutStateColor(row.state)}>
          <span className={styles.stateText} title={row.stateLabel}>
            {row.stateLabel}
          </span>
        </Badge>
      ),
    },
    {
      key: "flags",
      label: "Flags",
      render: (_, row) => {
        const badge = (override: Override) => (
          <Badge key={override.label} color="blue">
            {overrideText(override)}
          </Badge>
        );
        const shown = row.overrides.slice(0, MAX_OVERRIDE_BADGES);
        const hidden = row.overrides.slice(MAX_OVERRIDE_BADGES);
        return (
          <Inline gap="xs">
            {shown.map(badge)}
            {hidden.length > 0 && (
              <span title={hidden.map(overrideText).join(", ")}>
                <Badge color="blue">+{hidden.length}</Badge>
              </span>
            )}
            {row.fastTrack && <FastTrackBadge fastTrack={row.fastTrack} deviceId={row.deviceId} />}
            {row.hostWarnings.map((warning) => (
              <HostWarningBadge key={warning} warning={warning} />
            ))}
          </Inline>
        );
      },
    },
    {
      key: "eventsPerMinute",
      label: "Events/min",
      sortable: true,
      align: "right",
      render: (_, row) => (
        <Link to={deviceEventsPath(organisationId, row.deviceId)} className={styles.eventsLink}>
          {row.eventsPerMinute}
        </Link>
      ),
    },
    {
      key: "noiseDbfs",
      label: "Noise",
      sortable: true,
      hideOnMobile: true,
      render: (_, row) =>
        row.noiseDbfs === undefined ? <span className={styles.muted}>—</span> : <NoiseMeter dbfs={row.noiseDbfs} />,
    },
    {
      key: "lastUpdate",
      label: "Last update",
      className: styles.nowrap,
      sortable: true,
      secondary: true,
      hideOnMobile: true,
      render: (_, row) => formatLastUpdate(row.lastUpdate),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (_, row) => (
        <ActionMenu
          items={[
            { label: "Open device", icon: <EyeIcon />, onClick: () => history.push(devicePath(organisationId, row.deviceId)) },
            { label: "Events", icon: <ListIcon />, onClick: () => history.push(deviceEventsPath(organisationId, row.deviceId)) },
            { label: "Logs", icon: <FileTextIcon />, onClick: () => history.push(deviceLogsPath(organisationId, row.deviceId)) },
            { label: "Clock analytics", icon: <ClockIcon />, onClick: () => history.push(`/analytics/scout/${row.deviceId}/clock`) },
            { label: "Battery analytics", icon: <BoltIcon />, onClick: () => history.push(`/analytics/scout/${row.deviceId}/battery`) },
            {
              label: "Response times",
              icon: <BarChartIcon />,
              onClick: () => history.push(`/analytics/scout/${row.deviceId}/responseProcessingAnalytics`),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Table
      data={rows}
      columns={columns}
      rowKey="deviceId"
      defaultSortKey="name"
      search={{ placeholder: "Search devices..." }}
      meta={
        <span className={styles.muted}>
          {counts.listening} of {counts.total} devices listening
        </span>
      }
      actions={
        <Button icon={<EyeIcon />} onClick={() => setShowOffline(!showOffline)}>
          {showOffline ? "Hide offline" : "Show offline"}
        </Button>
      }
    />
  );
};

export default DevicesTable;
